// ============================================================================
// PLATFORM DELIVERY — shared helpers for reading episode_platform_ids rows
// ============================================================================

/** A platform row is only "live" once it carries a real external ID. */
export function isLivePlatformId(externalId?: string | null): boolean {
  return !!externalId && externalId !== "pending";
}

/**
 * True when the episode exists on at least one hosting platform.
 *
 * The `status` column is transient — a retryable platform hiccup can flip it to
 * "failed" while the sync queue retries — so delivery/edit gating keys off the
 * presence of a real external ID instead.
 */
export function hasLivePlatformDelivery(
  entries: { externalId?: string | null }[] | undefined,
): boolean {
  return (entries ?? []).some((p) => isLivePlatformId(p.externalId));
}

/** Podsense's episode artwork limits: 1400–3000 px square, JPG/PNG/WebP, 10 MB. */
export const PODSENSE_ARTWORK_RULES = {
  maxBytes: 10 * 1024 * 1024,
  minDimension: 1400,
  allowUpscale: false,
  requirementsText: "1400×1400–3000×3000 pixels. JPG, PNG, or WebP up to 10 MB.",
};

export function artworkRulesFor(hostingPlatform?: string | null) {
  return (hostingPlatform ?? "").toLowerCase() === "podsense" ? PODSENSE_ARTWORK_RULES : undefined;
}

/** Edited audio has a trustworthy MIME type; its original filename may still say MP3. */
export function needsMp3Conversion(hostingPlatform: string | null | undefined, mimeType: string): boolean {
  return hostingPlatform?.toLowerCase() === "megaphone" && mimeType !== "audio/mpeg";
}
